import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IVirtuousConnectedBy, VirtuousIntegration } from '@feathr/blackbox';
import type { IIntegrationSummaryCardProps } from '@feathr/components';
import { IntegrationSummaryCard } from '@feathr/components';

import VirtuousTabContent from '../VirtuousTabContent';

interface IProps {
  integration: VirtuousIntegration;
  /** Disconnects the integration. Rejects on failure, so the confirm modal stays open. */
  onDisconnect: () => Promise<void>;
  /** Disables Disconnect with this reason, e.g. while the page saves. */
  disabledReason?: string;
}

/** The card wants a name; a user without one is shown by email. */
export function toConnectedBy(
  connectedBy: IVirtuousConnectedBy | null | undefined,
): IIntegrationSummaryCardProps['connectedBy'] {
  const name = connectedBy?.name || connectedBy?.email;
  if (!name) {
    return null;
  }
  return connectedBy?.name && connectedBy.email ? { name, role: connectedBy.email } : { name };
}

function VirtuousSettingsTab({
  disabledReason,
  integration,
  onDisconnect,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();

  return (
    <VirtuousTabContent
      inactiveLabel={t('Connect Virtuous to see the connection details.')}
      integration={integration}
    >
      <IntegrationSummaryCard
        connectedBy={toConnectedBy(integration.get('connected_by'))}
        connectedOn={integration.get('date_created') ?? null}
        disconnectDisabledReason={disabledReason}
        name={'virtuous-connection-details'}
        onDisconnect={onDisconnect}
      />
    </VirtuousTabContent>
  );
}

export default VirtuousSettingsTab;
